import { createRoute, lazyRouteComponent, notFound } from '@tanstack/react-router';
import { isApiError } from '@/api/errors';
import { appRoute, type RouterContext } from '@/app/layout-routes';
import { requirePermissions } from '@/app/route-guards';
import {
  bonusGameQuery,
  bonusGamesQuery,
  bonusSearchSchema,
  drawSearchSchema,
  drawsQuery,
  jackpotSearchSchema,
  jackpotsQuery,
  progressQuery,
  progressSearchSchema,
  type BonusKind,
} from './api';
import { BonusNotFound } from './pages/bonus-not-found';

async function loadBonusGame(context: RouterContext, kind: BonusKind, id: string) {
  if (!/^\d+$/.test(id)) throw notFound();
  try {
    await context.queryClient.ensureQueryData(bonusGameQuery(kind, id));
  } catch (error) {
    // The settled service re-wraps its NotFoundException as a 400 ("Bonus game final with ID … not found").
    const missing = isApiError(error) && (error.code === 'not_found' || (kind === 'settled' && error.status === 400));
    if (missing) throw notFound();
    throw error;
  }
}

const listRoute = (kind: BonusKind) =>
  createRoute({
    getParentRoute: () => appRoute,
    path: kind === 'live' ? 'bonus-games' : 'bonus-games/settled',
    validateSearch: bonusSearchSchema,
    beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_GAMES'),
    loaderDeps: ({ search }) => search,
    loader: ({ context, deps }) => {
      void context.queryClient.prefetchQuery(bonusGamesQuery(kind, deps));
    },
    staticData: kind === 'settled' ? { crumbs: () => [{ label: 'Settled' }] } : {},
    component: lazyRouteComponent(
      () => import('./pages/bonus-games-page'),
      kind === 'live' ? 'LiveBonusGamesPage' : 'SettledBonusGamesPage',
    ),
  });

export const liveBonusGamesRoute = listRoute('live');
export const settledBonusGamesRoute = listRoute('settled');

export const bonusProgressRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'bonus-games/progress',
  validateSearch: progressSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_GAMES'),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(progressQuery(deps));
  },
  staticData: { crumbs: () => [{ label: 'Player progress' }] },
  component: lazyRouteComponent(() => import('./pages/progress-page'), 'ProgressDataPage'),
});

export const liveBonusGameDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'bonus-games/$bonusGameId',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_GAMES'),
  loader: ({ context, params }) => loadBonusGame(context, 'live', params.bonusGameId),
  staticData: { crumbs: (params) => [{ label: `Bonus game ${params.bonusGameId ?? ''}` }] },
  notFoundComponent: BonusNotFound,
  component: lazyRouteComponent(() => import('./pages/bonus-game-detail-page'), 'LiveBonusGameDetailPage'),
});

export const settledBonusGameDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'bonus-games/settled/$bonusGameId',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_GAMES'),
  loader: ({ context, params }) => loadBonusGame(context, 'settled', params.bonusGameId),
  staticData: {
    crumbs: (params) => [
      { label: 'Settled', href: '/bonus-games/settled' },
      { label: `Bonus game ${params.bonusGameId ?? ''}` },
    ],
  },
  notFoundComponent: BonusNotFound,
  component: lazyRouteComponent(() => import('./pages/bonus-game-detail-page'), 'SettledBonusGameDetailPage'),
});

export const jackpotTiersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jackpots',
  validateSearch: jackpotSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_GAMES'),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(jackpotsQuery(deps));
  },
  component: lazyRouteComponent(() => import('./pages/jackpots-page'), 'JackpotTiersPage'),
});

export const jackpotDrawsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'jackpots/draws',
  validateSearch: drawSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_GAMES'),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(drawsQuery(deps));
  },
  staticData: { crumbs: () => [{ label: 'Draws' }] },
  component: lazyRouteComponent(() => import('./pages/jackpots-page'), 'JackpotDrawsPage'),
});

export const bonusRoutes = [
  liveBonusGamesRoute,
  settledBonusGamesRoute,
  bonusProgressRoute,
  liveBonusGameDetailRoute,
  settledBonusGameDetailRoute,
  jackpotTiersRoute,
  jackpotDrawsRoute,
] as const;
